// CHEAT SHEET: https://gist.github.com/mrmartineau/a4b7dfc22dc8312f521b42bb3c9a7c1e
import AdvancedController from "../advanced-controller";
//import { fadeIn, fadeOut } from "../animation-fade";
import { breakpointDown, debounce } from "../utils";
import { gsap } from "gsap";
import { CSSRulePlugin } from "gsap/CSSRulePlugin";
import { Flip } from "gsap/Flip";

gsap.registerPlugin(CSSRulePlugin);
gsap.registerPlugin(Flip);

export class ServicesColumnController extends AdvancedController {
  static targets = ["content", "button"];
  static values = {
    contentIndex: { type: Number, default: 0 },
    state: { type: String, default: "NONE" },
  };
  static classes = ["open", "closed"];

  connect() {
    super.connect(...arguments);
    window.addEventListener(
      "resize",
      debounce(this._updateAfterResize.bind(this), 500)
    );
  }

  disconnect() {
    super.disconnect(...arguments);
    window.removeEventListener("resize", this._updateAfterResize);
  }

  _updateAfterResize() {
    if (this.stateValue === "OPEN") {
      const contentContainer = this.element.querySelector(
        ".services__column__content"
      );
      const activeContent = this.element.querySelector(
        ".services__content.active"
      );
      gsap.to(contentContainer, { height: activeContent.clientHeight });
    }
  }

  async activate({ target, params: { contentIndex } }) {
    for (const element of this.buttonTargets) {
      element.classList.remove("active");
    }
    if (!breakpointDown("md")) {
      const promises = [];
      if (this.stateValue !== "OPEN") {
        promises.push(this.setStateOpen());
      }
      const otherColumn = this.getSiblingControllers("services-column")[0];
      promises.push(otherColumn.setStateClosed());
      await Promise.all(promises);
      this.contentIndexValue = contentIndex;
      otherColumn.contentIndexValue = 0;
      target.classList.add("active");
    } else {
      this.contentIndexValue = contentIndex;
    }
  }

  setStateClosed() {
    return new Promise((resolve) => {
      if (this.stateValue === "CLOSED") {
        return resolve();
      }
      const state = Flip.getState(this.element, { props: "width" });
      this.element.classList.remove(this.openClass);
      this.element.classList.add(this.closedClass);

      const list = this.element.querySelectorAll("ul");
      const title = this.element.querySelector(
        ".services__column__header__title"
      );

      gsap.to([list, title], { autoAlpha: 0 });
      Flip.from(state, {
        duration: 0.75,
        onComplete: () => {
          this.stateValue = "CLOSED";
          resolve();
        },
      });
    });
  }

  setStateOpen() {
    return new Promise((resolve) => {
      if (this.stateValue === "OPEN") {
        return resolve();
      }
      const state = Flip.getState(this.element, { props: "width, height" });
      this.element.classList.remove(this.closedClass);
      this.element.classList.add(this.openClass);
      //hide list items
      const list = this.element.querySelectorAll("ul");
      gsap.to(list, { autoAlpha: 0 });
      //slide open
      Flip.from(state, {
        onComplete: () => {
          gsap.to(list, { autoAlpha: 1 });
          this.stateValue = "OPEN";
          resolve();
        },
        duration: 0.75,
      });
    });
  }

  setStateNeutral() {
    return new Promise((resolve) => {
      for (const element of this.buttonTargets) {
        element.classList.remove("active");
      }
      if (this.stateValue === "NONE") {
        return resolve();
      }
      const state = Flip.getState(this.element, { props: "width, height" });
      const targets = [];
      let contentContainer, title;

      const list = this.element.querySelectorAll("ul")[0];
      gsap.set(list, { height: list.clientHeight, overflow: "hidden" });
      targets.push(list);

      this.element.classList.remove(this.openClass);
      this.element.classList.remove(this.closedClass);

      if (this.stateValue === "OPEN") {
        //const content = this.element.querySelector(".services__content.active");
        //gsap.set(content, { height: content.clientHeight, overflow: "hidden" });
        //targets.push(content);
        contentContainer = this.element.querySelector(
          ".services__column__content"
        );
      }
      if (this.stateValue === "CLOSED") {
        title = this.element.querySelector(".services__column__header__title");
      }

      gsap.to(targets, {
        autoAlpha: 0,
        onComplete: () => {
          gsap.set(targets, {
            height: "auto",
            overflow: "auto",
          });
          if (this.stateValue === "OPEN") {
            gsap.set(contentContainer, { height: "auto" });
          }
        },
      });

      Flip.from(state, {
        onComplete: () => {
          gsap.to(list, { autoAlpha: 1 });
          if (this.stateValue === "CLOSED") {
            gsap.to(title, { autoAlpha: 1 });
          }
          this.stateValue = "NONE";
          resolve();
        },
        duration: 0.75,
      });
    });
  }

  async toggleState() {
    const otherColumn = this.getSiblingControllers("services-column")[0];
    switch (this.stateValue) {
      case "NONE":
        await Promise.all([this.setStateOpen(), otherColumn.setStateClosed()]);
        this.contentIndexValue = this.contentTarget.dataset.contentIndex;
        this.buttonTarget.classList.add("active");
        otherColumn.contentIndexValue = 0;
        break;
      default:
        await Promise.all([
          this.setStateNeutral(),
          otherColumn.setStateNeutral(),
        ]);
        this.contentIndexValue = 0;
        otherColumn.contentIndexValue = 0;
        break;
    }
  }
  contentIndexValueChanged(value, prevValue) {
    const oldContent = this.element.querySelector(
      `[data-content-index="${prevValue}"]`
    );
    const newContent = this.element.querySelector(
      `[data-content-index="${value}"]`
    );

    if (breakpointDown("md") && newContent) {
      const servicesController = this.getController("services");
      servicesController.openDialog(
        newContent.getElementsByClassName("services__content__inner")[0]
      );
    } else {
      const tl = gsap.timeline();
      tl.pause();
      if (oldContent) {
        const videoController = this.getSubControllers("video")[0];
        if (videoController) {
          videoController.pauseVideo();
        }
        gsap.set(oldContent, {
          position: "absolute",
          height: oldContent.clientHeight,
        });
        tl.to(
          oldContent,
          {
            autoAlpha: 0,
            onComplete: () => {
              oldContent.style.display = "none";
              oldContent.style.height = "auto";
              oldContent.classList.remove("active");
            },
          },
          0
        );
      }
      if (newContent) {
        gsap.set(newContent, { display: "block" });
        gsap.to(this.element.querySelector(".services__column__content"), {
          height: newContent.clientHeight,
        });
        tl.to(
          newContent,
          {
            autoAlpha: 1,
            onComplete: () => {
              newContent.classList.add("active");
            },
          },
          0
        );
      }
      tl.play();
    }
  }
}
export class ServicesController extends AdvancedController {
  static targets = ["dialog"];
  static values = {
    dialogOpen: { type: Boolean, default: false },
  };

  connect() {
    super.connect(...arguments);
    window.addEventListener(
      "resize",
      debounce(this._updateStatesAfterResize.bind(this), 500)
    );
  }
  disconnect() {
    super.disconnect(...arguments);
    window.removeEventListener("resize", this._updateStatesAfterResize);
  }
  async _updateStatesAfterResize() {
    if (breakpointDown("md")) {
      const chilControllers = this.getSubControllers("services-column");
      for (const controller of chilControllers) {
        await controller.setStateNeutral();
        controller.contentIndexValue = 0;
      }
    } else {
      if (this.dialogOpenValue) {
        const videoController = this.getSubControllers("video")[0];
        if (videoController && videoController.element.paused) {
          return;
        } else {
          this.closeDialog();
        }
      }
    }
  }

  openDialog(content) {
    this.dialogTarget.appendChild(content.cloneNode(true));
    gsap.set(this.dialogTarget, { autoAlpha: 0 });
    this.dialogTarget.showModal();
    this.dialogOpenValue = true;
    gsap.to(this.dialogTarget, { autoAlpha: 1 });
    document.body.style.overflow = "hidden";
  }
  closeDialog() {
    var rule = CSSRulePlugin.getRule(":backdrop");
    const dialog = this.dialogTarget;
    const controller = this;
    gsap.to(rule, {
      cssRule: { opacity: 0 },
      onComplete: () => {
        gsap.set(rule, { cssRule: { opacity: 1 } });
      },
    });
    gsap.to(dialog, {
      autoAlpha: 0,
      onComplete: () => {
        dialog.removeChild(dialog.lastChild);
        dialog.close();
        this.dialogOpenValue = false;
        document.body.style.overflow = "auto";
        controller.contentIndexValue = 0;
      },
    });
    const chilControllers = this.getSubControllers("services-column");
    for (const controller of chilControllers) {
      controller.contentIndexValue = 0;
    }
  }
}

export class VideoController extends AdvancedController {
  static values = {
    video1080: { type: String, default: "" },
    video720: { type: String, default: "" },
  };

  connect() {
    super.connect(...arguments);
    this._updateVideoSrc();
    window.addEventListener(
      "resize",
      debounce(this._updateVideoSrc.bind(this), 1000)
    );
  }

  _updateVideoSrc() {
    const videoSrc = breakpointDown("sm")
      ? this.video720Value
      : this.video1080Value;
    if (this.element.src !== videoSrc && this.element.paused) {
      this.element.src = videoSrc;
    }
  }

  pauseVideo() {
    this.element.pause();
  }
}
